import type { ProductScopeBinding } from "@/domain/auth/ProductSession";
import {
	artistPublicAssetURL,
	browserArtistRuntimeConfiguration,
} from "@/domain/auth/AuthProviderConfiguration";
import type { Route } from "@/infrastructure/router/types";
import { artistSupabase } from "@/infrastructure/supabase/client";
import circleDanceManifest from "@/content/tutorial/circle-dance-v1.json";
import { listLocalTrackAssets } from "@/domain/media/LocalTrackAssetStore";
import {
	listLocalNotes,
	loadLocalNote,
	type LocalNoteAsset,
} from "@/domain/social/LocalNoteStore";

export interface DiscoveryTrack {
	readonly id: string;
	readonly title: string;
	readonly artist: string;
	readonly durationSeconds: number;
	readonly genre: string;
	readonly description: string;
	readonly artwork: "violet" | "amber" | "blue" | "lime" | "rose";
	readonly playableRequestId: string | null;
	readonly artworkURL?: string;
	readonly playbackURL?: string;
}

function publicHttps(value: unknown): string | null {
	if (typeof value !== "string") return null;
	try {
		const url = new URL(value);
		return url.protocol === "https:" ? url.href : null;
	} catch {
		return null;
	}
}

export interface InspirationAuthor {
	readonly name: string;
	readonly avatarURL?: string;
}

export interface Inspiration {
	readonly id: string;
	readonly title: string;
	readonly prompt: string;
	readonly note: string;
	readonly artwork: DiscoveryTrack["artwork"];
	/** Optional cover media URL — mirrors iOS `thread.mediaURLs.first`. */
	readonly mediaURL?: string;
	/** Optional author — mirrors iOS `thread.author`. */
	readonly author?: InspirationAuthor;
	/** Optional like count — mirrors iOS `thread.likeCount`. */
	readonly likeCount?: number;
	/** Optional liked state — mirrors iOS `thread.isLiked`. */
	readonly isLiked?: boolean;
}

export interface DiscoveryCircle {
	readonly id: string;
	readonly name: string;
	readonly description: string;
	readonly playlistId: string;
	readonly artwork: DiscoveryTrack["artwork"];
}

export interface DiscoveryPlaylist {
	readonly id: string;
	readonly name: string;
	readonly description: string;
	readonly createdBy: string;
	readonly ownerUserId: string | null;
	readonly isSystem: boolean;
	readonly trackIds: readonly string[];
	readonly tracks: readonly DiscoveryTrack[];
}

export interface DiscoverySnapshot {
	readonly featured: readonly DiscoveryTrack[];
	readonly circles: readonly DiscoveryCircle[];
	readonly trendingFallback: readonly DiscoveryTrack[];
	readonly recent: readonly DiscoveryTrack[];
	readonly works: readonly DiscoveryTrack[];
}

export interface DiscoverySearchResult {
	readonly tracks: readonly DiscoveryTrack[];
	readonly playlists: readonly DiscoveryPlaylist[];
	readonly inspirations: readonly Inspiration[];
}

export interface DiscoveryRepository {
	loadHome(scope: ProductScopeBinding | null): Promise<DiscoverySnapshot>;
	loadInspirations(
		scope?: ProductScopeBinding | null,
	): Promise<readonly Inspiration[]>;
	loadInspiration(
		id: string,
		scope?: ProductScopeBinding | null,
	): Promise<Inspiration | null>;
	searchArtist(
		query: string,
		scope: ProductScopeBinding | null,
	): Promise<DiscoverySearchResult>;
	loadPlaylist(
		id: string,
		scope: ProductScopeBinding | null,
	): Promise<DiscoveryPlaylist | null>;
	savePlaylist(
		playlist: DiscoveryPlaylist,
		scope: ProductScopeBinding,
	): Promise<DiscoveryPlaylist>;
	loadRecent(scope: ProductScopeBinding): Promise<readonly DiscoveryTrack[]>;
	recordRecent(requestId: string, scope: ProductScopeBinding): Promise<void>;
	clearRecent(scope: ProductScopeBinding): Promise<void>;
}

const RECENT_PREFIX = "artist.discovery.recent.v1";
const PLAYLIST_PREFIX = "artist.discovery.playlist.v1";

function storageKey(
	prefix: string,
	scope: ProductScopeBinding,
	suffix = "",
): string {
	return `${prefix}:${scope.ownerUserId}:${scope.scopeEpoch}${suffix}`;
}

function parseStringArray(value: string | null): string[] {
	if (value === null) return [];
	try {
		const parsed: unknown = JSON.parse(value);
		return Array.isArray(parsed) &&
			parsed.every((item) => typeof item === "string")
			? parsed
			: [];
	} catch {
		return [];
	}
}

const ARTWORK_PALETTE = ["lime", "violet", "blue", "amber", "rose"] as const;
const discoveryRuntime = browserArtistRuntimeConfiguration();

function bundledPublicTrack(id: string): DiscoveryTrack | null {
	if (id !== circleDanceManifest.template_track_id) return null;
	return {
		id: `public:${id}`,
		title: circleDanceManifest.title,
		artist: "Artist Tutorial",
		durationSeconds: Math.round(
			circleDanceManifest.hosted_assets.audio_lossless.duration_seconds,
		),
		genre: "Tutorial",
		description: "Artist Tutorial · Tutorial",
		artwork: "violet",
		playableRequestId: null,
		artworkURL: artistPublicAssetURL(
			discoveryRuntime,
			circleDanceManifest.hosted_assets.cover,
		),
		playbackURL: artistPublicAssetURL(
			discoveryRuntime,
			circleDanceManifest.hosted_assets.audio_lossless,
		),
	};
}

function noteToInspiration(note: LocalNoteAsset, index = 0): Inspiration {
	const firstLine = note.body.split(/\r?\n/, 1)[0]?.trim() ?? "";
	const title = firstLine.length > 60 ? `${firstLine.slice(0, 60)}…` : firstLine;
	return {
		id: note.id,
		title: title || "未命名笔记",
		prompt: note.body,
		note: note.visibility === "public" ? "公开笔记" : "仅自己可见",
		artwork: ARTWORK_PALETTE[index % ARTWORK_PALETTE.length] ?? "lime",
		author: { name: note.authorName },
		likeCount: note.likeCount,
		isLiked: note.isLiked,
	};
}

export class BrowserDiscoveryRepository implements DiscoveryRepository {
	private productionCache: readonly DiscoveryTrack[] = [];

	async loadPublicTrack(id: string): Promise<DiscoveryTrack | null> {
		const normalized = id.trim().replace(/^public:/u, "");
		if (normalized.length === 0) return null;
		const bundled = bundledPublicTrack(normalized);
		if (bundled !== null) return bundled;
		const cached = this.productionCache.find(
			(track) => track.id.replace(/^public:/u, "") === normalized,
		);
		if (cached !== undefined) return cached;
		return (await this.queryProductionTracks(normalized))[0] ?? null;
	}

	async loadPublicQueue(): Promise<readonly DiscoveryTrack[]> {
		return this.productionTracks();
	}

	private async productionTracks(forceRefresh = false): Promise<readonly DiscoveryTrack[]> {
		if (!forceRefresh && this.productionCache.length > 0) return this.productionCache;
		const tracks = await this.queryProductionTracks();
		this.productionCache = tracks;
		return tracks;
	}

	private async queryProductionTracks(id: string | null = null): Promise<readonly DiscoveryTrack[]> {
		try {
			const query = artistSupabase()
				.from("tracks")
				.select(
					"id,title,duration_seconds,lossy_standard_url,lossy_low_url,lossless_url,artwork_url,genre,artist:artists(name)",
				)
				.eq("encode_status", "ready")
				.eq("moderation_state", "approved")
				.eq("visibility_state", "public")
				.or("lossy_standard_url.not.is.null,lossy_low_url.not.is.null,lossless_url.not.is.null");
			const request = id === null
				? query
					.order("like_count", { ascending: false, nullsFirst: false })
					.order("created_at", { ascending: false })
					.limit(40)
				: query.eq("id", id).limit(1);
			const { data, error } = await request;
			if (error) return [];
			const tracks = (data ?? []).flatMap((row, index) => {
				const artistValue = Array.isArray(row.artist) ? row.artist[0] : row.artist;
				const artistName =
					typeof artistValue === "object" &&
					artistValue !== null &&
					"name" in artistValue &&
					typeof artistValue.name === "string"
						? artistValue.name.trim()
						: "Artist";
				const artworkURL = publicHttps(row.artwork_url);
					const playbackURL =
						publicHttps(row.lossy_standard_url) ??
						publicHttps(row.lossy_low_url) ??
						publicHttps(row.lossless_url);
				if (
					typeof row.id !== "string" ||
					typeof row.title !== "string" ||
					artworkURL === null ||
					playbackURL === null
				) return [];
				return [{
					id: `public:${row.id}`,
					title: row.title,
					artist: artistName || "Artist",
					durationSeconds:
						typeof row.duration_seconds === "number"
							? Math.max(0, Math.round(row.duration_seconds))
							: 0,
					genre:
						typeof row.genre === "string" && row.genre.trim().length > 0
							? row.genre.trim()
							: "AI Music",
					description: `${artistName || "Artist"} · ${
						typeof row.genre === "string" && row.genre.trim().length > 0
							? row.genre.trim()
							: "AI Music"
					}`,
					artwork: ARTWORK_PALETTE[index % ARTWORK_PALETTE.length] ?? "violet",
					playableRequestId: null,
					artworkURL,
					playbackURL,
				} satisfies DiscoveryTrack];
			});
			return tracks;
		} catch {
			return [];
		}
	}

	private async works(
		scope: ProductScopeBinding | null,
	): Promise<DiscoveryTrack[]> {
		if (scope === null) return [];
		const assets = await listLocalTrackAssets(scope);
		return assets.map((asset, index) => ({
			id: asset.id,
			title: asset.title || asset.audioFilename,
			artist: "你",
			durationSeconds: asset.durationSeconds,
			genre: "本地作品",
			description: asset.audioFilename,
			artwork: (["lime", "violet", "blue", "amber", "rose"] as const)[
				index % 5
			] ?? "lime",
			playableRequestId: asset.id,
		}));
	}

	async loadHome(
		scope: ProductScopeBinding | null,
	): Promise<DiscoverySnapshot> {
		const [works, production] = await Promise.all([
			this.works(scope),
			this.productionTracks(),
		]);
		const recent = scope === null ? [] : await this.loadRecent(scope);
		const circles = production.slice(0, 12).reduce<DiscoveryCircle[]>((values, track, index) => {
			if (index % 4 !== 0) return values;
			const group = production.slice(index, index + 4);
			values.push({
				id: `public:circle:${index / 4}`,
				name: track.genre,
				description: group.map((item) => item.title).join(" · "),
				playlistId: `public:playlist:${index / 4}`,
				artwork: track.artwork,
			});
			return values;
		}, []);
		return {
			featured: production.length > 0 ? production.slice(0, 10) : works,
			circles,
			trendingFallback: production.slice(5, 15),
			recent,
			works,
		};
	}

	async loadInspirations(
		scope: ProductScopeBinding | null = null,
	): Promise<readonly Inspiration[]> {
		const [notes, production] = await Promise.all([
			scope === null ? Promise.resolve([]) : listLocalNotes(scope),
			this.productionTracks(),
		]);
		return [
			...notes.map(noteToInspiration),
			...production.slice(0, 12).map((track) => ({
				id: `inspiration:${track.id}`,
				title: track.title,
				prompt: track.genre,
				note: track.description,
				artwork: track.artwork,
				...(track.artworkURL === undefined ? {} : { mediaURL: track.artworkURL }),
				author: { name: track.artist },
			} satisfies Inspiration)),
		];
	}

	async loadInspiration(
		id: string,
		scope: ProductScopeBinding | null = null,
	): Promise<Inspiration | null> {
		if (scope !== null) {
			const note = await loadLocalNote(scope, id);
			if (note !== null) return noteToInspiration(note);
		}
		const publicId = id.startsWith("inspiration:") ? id.slice("inspiration:".length) : id;
		const track = await this.loadPublicTrack(publicId);
		return track === null ? null : {
			id,
			title: track.title,
			prompt: track.genre,
			note: track.description,
			artwork: track.artwork,
			...(track.artworkURL === undefined ? {} : { mediaURL: track.artworkURL }),
			author: { name: track.artist },
		};
	}

	async searchArtist(
		query: string,
		scope: ProductScopeBinding | null,
	): Promise<DiscoverySearchResult> {
		const normalized = query.trim().toLocaleLowerCase();
		if (normalized.length === 0)
			return { tracks: [], playlists: [], inspirations: [] };
		const [works, production, inspirations] = await Promise.all([
			this.works(scope), this.productionTracks(),
			this.loadInspirations(scope),
		]);
		const matches = (values: readonly string[]) =>
			values.some((value) => value.toLocaleLowerCase().includes(normalized));
		return {
			tracks: [...production, ...works].filter((track) =>
				matches([track.title, track.artist, track.genre, track.description]),
			),
			playlists: [],
			inspirations: inspirations.filter((item) =>
				matches([item.title, item.prompt, item.note]),
			),
		};
	}

	async loadPlaylist(
		id: string,
		scope: ProductScopeBinding | null,
	): Promise<DiscoveryPlaylist | null> {
		if (id.startsWith("public:playlist:")) {
			const index = Number(id.slice("public:playlist:".length));
			if (!Number.isInteger(index) || index < 0) return null;
			const tracks = (await this.productionTracks()).slice(index * 4, index * 4 + 4);
			if (tracks.length === 0) return null;
			return {
				id,
				name: tracks[0]?.genre ?? "Artist",
				description: tracks.map((track) => track.title).join(" · "),
				createdBy: "Artist",
				ownerUserId: null,
				isSystem: true,
				trackIds: tracks.map((track) => track.id),
				tracks,
			};
		}
		if (id !== "your-ready-works" || scope === null) return null;
		const works = await this.works(scope);
		const persisted =
			typeof window === "undefined"
				? null
				: window.localStorage.getItem(
						storageKey(PLAYLIST_PREFIX, scope, `:${id}`),
					);
		let name = "你的可播放作品";
		let order = works.map((track) => track.id);
		let hasPersistedMembership = false;
		if (persisted !== null) {
			try {
				const value: unknown = JSON.parse(persisted);
				if (typeof value === "object" && value !== null) {
					const candidate = value as { name?: unknown; trackIds?: unknown };
					if (
						typeof candidate.name === "string" &&
						candidate.name.trim() !== ""
					)
						name = candidate.name;
					if (
						Array.isArray(candidate.trackIds) &&
						candidate.trackIds.every((item) => typeof item === "string")
					) {
						order = candidate.trackIds;
						hasPersistedMembership = true;
					}
				}
			} catch {
				/* malformed owner-local metadata is ignored */
			}
		}
		const map = new Map(works.map((track) => [track.id, track]));
		const tracks = order.flatMap((trackId) => map.get(trackId) ?? []);
		if (!hasPersistedMembership)
			for (const track of works)
				if (!order.includes(track.id)) tracks.push(track);
		return {
			id,
			name,
			description:
				"仅包含当前账户与当前 scope 中具有精确 ready 媒体凭证的作品。",
			createdBy: "你",
			ownerUserId: scope.ownerUserId,
			isSystem: false,
			trackIds: tracks.map((track) => track.id),
			tracks,
		};
	}

	async savePlaylist(
		playlist: DiscoveryPlaylist,
		scope: ProductScopeBinding,
	): Promise<DiscoveryPlaylist> {
		if (playlist.isSystem || playlist.ownerUserId !== scope.ownerUserId)
			throw new Error("playlist is not editable in this owner scope");
		window.localStorage.setItem(
			storageKey(PLAYLIST_PREFIX, scope, `:${playlist.id}`),
			JSON.stringify({ name: playlist.name, trackIds: playlist.trackIds }),
		);
		return playlist;
	}

	async loadRecent(
		scope: ProductScopeBinding,
	): Promise<readonly DiscoveryTrack[]> {
		const [works, production] = await Promise.all([
			this.works(scope),
			this.productionTracks(),
		]);
		const ids =
			typeof window === "undefined"
				? []
				: parseStringArray(
						window.localStorage.getItem(storageKey(RECENT_PREFIX, scope)),
					);
		const map = new Map(
			[...production, ...works].map((track) => [track.playableRequestId ?? track.id, track]),
		);
		return ids.flatMap((id) => map.get(id) ?? []);
	}

	async recordRecent(
		requestId: string,
		scope: ProductScopeBinding,
	): Promise<void> {
		const key = storageKey(RECENT_PREFIX, scope);
		const current = parseStringArray(window.localStorage.getItem(key));
		window.localStorage.setItem(
			key,
			JSON.stringify(
				[requestId, ...current.filter((id) => id !== requestId)].slice(0, 50),
			),
		);
	}

	async clearRecent(scope: ProductScopeBinding): Promise<void> {
		window.localStorage.removeItem(storageKey(RECENT_PREFIX, scope));
	}
}

export const browserDiscoveryRepository = new BrowserDiscoveryRepository();

export function playbackHref(
	track: DiscoveryTrack,
	queue: readonly DiscoveryTrack[],
): Route | null {
	if (track.playbackURL) {
		const params = new URLSearchParams({ remote: track.id });
		return `/player?${params.toString()}` as Route;
	}
	if (track.playableRequestId === null) return null;
	const playable = queue.flatMap((item) => item.playableRequestId ?? []);
	const params = new URLSearchParams({ request: track.playableRequestId });
	if (playable.length > 1) params.set("queue", playable.join(","));
	return `/player?${params.toString()}` as Route;
}
